import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@ohpen/enso-ui';

import { ConfigTableActions } from '@config/components';
import { useConfigContext } from '@config/context';
import { CONFIG_PAGINATION, CONFIG_SCROLL } from '@config/utils';
import type { PermissionRow } from '../interfaces';
import { permissionColumns, RESOURCE_LIST_NAME } from '../tableConfig';

export const PermissionsList = (): ReactElement => {
  const { t } = useTranslation();
  const { configuration, permissionCatalogue, roleCatalogue } = useConfigContext();

  // Which roles hold each permission. The roles are configuration, the permissions are not.
  const rolesByPermission = useMemo(() => {
    const held: [string, string][] = roleCatalogue
      ? roleCatalogue.flatMap(entry => entry.permissions.map(permission => [permission, entry.role] as [string, string]))
      : Object.entries(configuration?.groupPermissions ?? {}).flatMap(([role, resources]) =>
          Object.entries(resources).flatMap(([resource, actions]) =>
            actions.map(action => [`${resource}:${action}`, role] as [string, string]),
          ),
        );

    const map = new Map<string, string[]>();
    held.forEach(([permission, role]) => map.set(permission, [...(map.get(permission) ?? []), role]));

    return map;
  }, [configuration, roleCatalogue]);

  const dataSource: PermissionRow[] = useMemo(() => {
    // The catalogue is the full set of permissions the application declares. Falling back to the
    // roles shows only the ones somebody already holds, which is a smaller and less useful list.
    const entries: Omit<PermissionRow, 'roles'>[] =
      permissionCatalogue?.map(entry => ({ ...entry, permission: `${entry.resource}:${entry.action}` })) ??
      // A key of the roles carries whatever the host wrote in the role's permissions, which need
      // not name an action, so the key itself stays the identity of the row.
      [...rolesByPermission.keys()].map(permission => {
        const [resource, action = ''] = permission.split(':');
        return { permission, resource, resourceLabel: resource, action };
      });

    return entries
      .map(entry => ({
        ...entry,
        roles: (rolesByPermission.get(entry.permission) ?? []).sort((a, b) => a.localeCompare(b)),
      }))
      .sort((a, b) => a.resourceLabel.localeCompare(b.resourceLabel) || a.action.localeCompare(b.action));
  }, [permissionCatalogue, rolesByPermission]);

  const valueLists = useMemo(() => {
    const resources: Record<string, string> = {};
    dataSource.forEach(row => {
      resources[row.resource] = row.resourceLabel;
    });

    return { [RESOURCE_LIST_NAME]: resources };
  }, [dataSource]);

  return (
    <Table
      id="configPermissions"
      rowKey="permission"
      dataSource={dataSource}
      columns={permissionColumns}
      valueLists={valueLists}
      pagination={CONFIG_PAGINATION}
      scroll={CONFIG_SCROLL}
      itemsLabel={t('config.sections.permissions')}
    >
      <ConfigTableActions />
    </Table>
  );
};
